/** Transforms BFF config file of configurablePage app to the JSON structure expected by configurable-page-service */

import {
  TConfig,
  TConfigBackend,
  TPreviewBlock,
  TPreviewBlockBackend,
  TSearchRequest,
  TSearchRequestBackend,
  TSection,
} from '../../client/js/types/TConfigurablePage';
import isEmpty from 'lodash/isEmpty';

const transformHeader = (header: TConfig['header']) => {
  if (!header || !(header.imageUrl || header.body)) {
    return;
  }

  return {
    body: header.body,
    media: {
      type: 'image',
      pictures: [{ large: header.imageUrl }],
    },
  };
};

const transformAttributeIds = (attributeIds: string[]) => attributeIds.map((attrId) => ({ attributeValueId: attrId }));

const transformAttributeRanges = (attributeRanges: string[]) =>
  attributeRanges.map((attributeRange) => {
    const [attributeKey, from, to] = attributeRange.split(':');
    // from/to arrive as strings from the config; backend type declares number but runtime sends strings
    return { attributeKey, from, to } as unknown as { attributeKey: string; from: number; to: number };
  });

const transformAttributeKeys = (attributeKeys: string[]) =>
  attributeKeys.map((attributeKeyValue) => {
    const [attributeKey, attributeValueKey] = attributeKeyValue.split(':');
    return { attributeKey, attributeValueKey };
  });

export const transformSearchRequest = (searchRequest: TSearchRequest): TSearchRequestBackend | void => {
  if (!searchRequest) {
    return;
  }

  const {
    l1CategoryId,
    l2CategoryId,
    attributeIds,
    attributeRanges,
    attributeKeys,
    sorting,
    query,
    offset,
    limit,
    sellerIds,
  } = searchRequest;

  const id = l2CategoryId || l1CategoryId;

  const parentId = l2CategoryId ? l1CategoryId : undefined;

  return {
    filters: {
      ...(id ? { categories: [{ id, parentId }] } : {}),
      ...(query ? { query: { text: query } } : {}),
      ...(attributeIds?.length ? { attributesById: transformAttributeIds(attributeIds) } : {}),
      ...(attributeKeys?.length ? { attributesByKey: transformAttributeKeys(attributeKeys) } : {}),
      ...(attributeRanges?.length ? { attributeRanges: transformAttributeRanges(attributeRanges) } : {}),
      ...(sellerIds?.length ? { sellerIds } : {}),
    },
    ...(sorting?.sortBy ? { sorting: { sortOrder: sorting.sortOrder, sortBy: sorting.sortBy } } : {}),
    limit: limit,
    offset: offset || 0,
  };
};

const transformPreviewBlocks = (previewBlocks: TPreviewBlock[]): TPreviewBlockBackend[] | void => {
  if (!previewBlocks) {
    return;
  }

  return previewBlocks.map((previewBlock) => ({
    body: previewBlock.body,
    action: previewBlock.action,
    searchRequest: transformSearchRequest(previewBlock.searchRequest) as TSearchRequest,
  }));
};

const transformSections = (sections: TSection[]) =>
  sections.map((section) => ({
    type: section.type,
    body: section.body,
    action: section.action,
    ...(!section.seoContent && {
      content: {
        excludeAdmarkt: section.content.excludeAdmarkt,
        visualization: section.content.visualization,
        ...(!isEmpty(section.content.feedRequest) && { feedRequest: section.content.feedRequest }),
        ...(!isEmpty(section.content.searchRequest) && {
          searchRequest: transformSearchRequest(section.content.searchRequest),
        }),
        ...(!isEmpty(section.content.previewBlocks?.[0]) && {
          previewBlocks: transformPreviewBlocks(section.content.previewBlocks),
        }),
      },
    }),
    seoContent: section.seoContent,
  }));

const clientConfigToBackendConfig = (config: TConfig): TConfigBackend => ({
  header: transformHeader(config.header),
  metaData: config.metaData,
  action: config.action,
  // content is absent on seoContent sections; TSectionBackend requires it but runtime is correct
  sections: transformSections(config.sections) as TConfigBackend['sections'],
});

export default clientConfigToBackendConfig;
